import {
	addMonths,
	eachDayOfInterval,
	endOfMonth,
	endOfWeek,
	format,
	isSameDay,
	isSameMonth,
	isToday,
	startOfMonth,
	startOfWeek,
	subMonths
} from "date-fns"
import { AnimatePresence, motion } from "motion/react"
import {
	CalendarClockIcon,
	CheckCircle2Icon,
	ChevronLeftIcon,
	ChevronRightIcon,
	UserCircleIcon
} from "lucide-react"
import { useMemo, useState } from "react"
import { Link } from "~/components/Link"
import { toast } from "sonner"
import { PriorityIcon } from "~/components/icons/priority"
import { StatusIcon } from "~/components/icons/status"
import { UnsavedChanges } from "~/components/UnsavedChanges"
import { EntityIdentity } from "~/components/EntityIdentity"
import { Button } from "~/components/ui/button"
import { ScrollArea } from "~/components/ui/scroll-area"
import { Switch } from "~/components/ui/switch"
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from "~/components/ui/tooltip"
import { usePermission } from "~/hooks/usePermission"
import { TeamPermissions } from "~/lib/permissions"
import { cn, fancyIssueId, formatDate } from "~/lib/utils"
import type { RouterOutputs } from "~/trpc/react"
import { api } from "~/trpc/react"

export function IssueCalendar({
	teamIssueData,
	onIssueOpen
}: {
	teamIssueData: RouterOutputs["issues"]["getTeamIssues"]
	onIssueOpen?: (
		issue: RouterOutputs["issues"]["getTeamIssues"]["issues"][number]
	) => void
}) {
	const [month, setMonth] = useState(() => startOfMonth(new Date()))
	const [activeIssueId, setActiveIssueId] = useState<string | null>(null)
	const [dragOverDate, setDragOverDate] = useState<string | null>(null)
	const [pendingDueDates, setPendingDueDates] = useState<Record<string, Date>>(
		{}
	)
	const [hideEmptyDates, setHideEmptyDates] = useState(false)
	const { hasTeamPermission, teamLoaded } = usePermission()
	const canEdit = hasTeamPermission(TeamPermissions.EDIT_ISSUES)
	const utils = api.useUtils()
	const editIssue = api.issues.edit.useMutation()
	const calendarDays = useMemo(
		() =>
			eachDayOfInterval({
				start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
				end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 })
			}),
		[month]
	)
	const datedIssues = teamIssueData.issues.filter((issue) => issue.dueDate)
	const pendingIssueIds = useMemo(
		() => new Set(Object.keys(pendingDueDates)),
		[pendingDueDates]
	)
	const visibleIssues = datedIssues.map((issue) => ({
		...issue,
		dueDate: pendingDueDates[issue.id] ?? issue.dueDate
	}))
	const issuesByDay = calendarDays.reduce<
		Record<string, RouterOutputs["issues"]["getTeamIssues"]["issues"][number][]>
	>((acc, day) => {
		const key = format(day, "yyyy-MM-dd")
		acc[key] = visibleIssues.filter(
			(issue) => issue.dueDate && isSameDay(issue.dueDate, day)
		)
		return acc
	}, {})
	const monthLabel = format(month, "MMMM")
	const currentMonthIssues = visibleIssues.filter(
		(issue) => issue.dueDate && isSameMonth(issue.dueDate, month)
	)
	const calendarDaysToShow = hideEmptyDates
		? calendarDays.filter((day) => {
				const key = format(day, "yyyy-MM-dd")
				return isSameMonth(day, month) && (issuesByDay[key]?.length ?? 0)
			})
		: calendarDays
	const pendingCount = Object.keys(pendingDueDates).length
	const activeIssue = activeIssueId
		? datedIssues.find((issue) => issue.id === activeIssueId)
		: null

	return (
		<div className="flex h-full flex-col">
			<div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-stroke px-4 py-3">
				<div className="flex items-center gap-2">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setMonth((current) => subMonths(current, 1))}
					>
						<ChevronLeftIcon className="size-4" />
					</Button>
					<div className="w-40 text-center text-sm font-semibold text-text-main">
						{format(month, "MMMM yyyy")}
					</div>
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setMonth((current) => addMonths(current, 1))}
					>
						<ChevronRightIcon className="size-4" />
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setMonth(startOfMonth(new Date()))}
					>
						Today
					</Button>
				</div>
				<div className="flex items-center gap-3 text-xs font-medium text-text-small">
					<div className="flex items-center gap-2">
						<Switch
							id="hide-empty-calendar-dates"
							checked={hideEmptyDates}
							onCheckedChange={setHideEmptyDates}
						/>
						<label htmlFor="hide-empty-calendar-dates">
							{hideEmptyDates ? "Show" : "Hide"} empty dates in {monthLabel}.
						</label>
					</div>
					<span className="flex items-center gap-1.5">
						<CalendarClockIcon className="size-4" />
						{currentMonthIssues.length} due in {monthLabel}
					</span>
					{pendingCount ? (
						<span className="flex items-center gap-1.5 text-orbit-text">
							<CheckCircle2Icon className="size-4" />
							{pendingCount} pending
						</span>
					) : null}
				</div>
			</div>
			{currentMonthIssues.length ? (
				<ScrollArea className="min-h-0 flex-1">
					<div className="min-w-255 p-4">
						<div
							className={cn(
								"grid overflow-hidden rounded-lg border border-stroke bg-background shadow-[0_10px_34px_rgba(0,0,0,0.14)]",
								hideEmptyDates
									? "grid-cols-[repeat(auto-fit,minmax(220px,1fr))]"
									: "grid-cols-7"
							)}
						>
							{hideEmptyDates
								? null
								: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
										(day) => (
											<div
												key={day}
												className="border-r border-b border-stroke bg-background-tertiary/60 px-3 py-2 text-xs font-semibold text-text-small last:border-r-0"
											>
												{day}
											</div>
										)
									)}
							<AnimatePresence initial={false}>
								{calendarDaysToShow.map((day) => {
									const dayKey = format(day, "yyyy-MM-dd")
									const issues = issuesByDay[dayKey] ?? []

									return (
										<motion.div
											key={dayKey}
											layout
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											exit={{ opacity: 0 }}
											className="h-full"
										>
											{/* oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- Shit the FUCK down, linter */}
											<div
												onDragOver={(event) => {
													if (!activeIssueId) return
													event.preventDefault()
													setDragOverDate(dayKey)
												}}
												onDragLeave={() => {
													if (dragOverDate === dayKey) setDragOverDate(null)
												}}
												onDrop={(event) => {
													event.preventDefault()
													setDragOverDate(null)
													const issueId =
														event.dataTransfer.getData("text/plain") ||
														activeIssueId
													const issue = datedIssues.find(
														(issue) => issue.id === issueId
													)
													if (!issue?.dueDate) return

													const [year, selectedMonth, selectedDay] = dayKey
														.split("-")
														.map(Number)
													const nextDueDate = new Date(
														year ?? 0,
														(selectedMonth ?? 1) - 1,
														selectedDay ?? 1,
														issue.dueDate.getHours(),
														issue.dueDate.getMinutes(),
														issue.dueDate.getSeconds(),
														issue.dueDate.getMilliseconds()
													)

													if (isSameDay(issue.dueDate, nextDueDate)) {
														setPendingDueDates(
															({ [issue.id]: _, ...rest }) => rest
														)
														return
													}

													setPendingDueDates((current) => ({
														...current,
														[issue.id]: nextDueDate
													}))
												}}
												className={cn(
													"h-full min-h-36 border-b border-stroke/70 bg-background p-2 transition-colors",
													(day.getDay() !== 0 || hideEmptyDates) &&
														"border-r border-stroke/70",
													!isSameMonth(day, month) &&
														"bg-background-secondary/50 text-text-small",
													dragOverDate === dayKey &&
														"bg-primary/10 ring-1 ring-inset ring-primary/70"
												)}
											>
												<div className="mb-2 flex items-center justify-between gap-2">
													<span
														className={cn(
															"flex size-6 items-center justify-center rounded-md text-xs font-medium text-text-small",
															isToday(day) &&
																"bg-primary text-primary-foreground"
														)}
													>
														{format(day, "d")}
													</span>
													{issues.length ? (
														<span className="text-[11px] font-medium text-text-small">
															{issues.length}
														</span>
													) : null}
												</div>
												<div className="space-y-1.5">
													{issues.map((issue) => (
														<div key={issue.id}>
															<TooltipProvider>
																<Tooltip>
																	<TooltipTrigger asChild>
																		<Link
																			to={`/${issue.team.organization.slug}/${issue.team.slug}/${onIssueOpen ? `queue?issue=${issue.friendlyId}` : issue.friendlyId}`}
																			onClick={(event) => {
																				if (
																					onIssueOpen &&
																					event.button === 0 &&
																					!event.metaKey &&
																					!event.ctrlKey &&
																					!event.shiftKey &&
																					!event.altKey
																				) {
																					event.preventDefault()
																					onIssueOpen(issue)
																				}
																			}}
																			draggable={canEdit && teamLoaded}
																			onDragStart={(event) => {
																				setActiveIssueId(issue.id)
																				event.dataTransfer.effectAllowed =
																					"move"
																				event.dataTransfer.setData(
																					"text/plain",
																					issue.id
																				)
																			}}
																			onDragEnd={() => {
																				setActiveIssueId(null)
																				setDragOverDate(null)
																			}}
																			className={cn(
																				"flex min-h-8 items-center gap-1.5 rounded-md border border-transparent bg-background-tertiary/80 px-2 py-1 text-left text-xs text-text-main transition hover:border-stroke-hover hover:bg-active",
																				canEdit &&
																					teamLoaded &&
																					"cursor-grab active:cursor-grabbing",
																				activeIssueId === issue.id &&
																					"opacity-50",
																				pendingIssueIds.has(issue.id) &&
																					"border-primary/80 bg-primary/10 text-text-main shadow-[0_0_0_1px_rgba(255,255,255,0.08)]"
																			)}
																		>
																			<PriorityIcon
																				priority={issue.priority}
																				className="size-3.5"
																			/>
																			<span className="min-w-0 flex-1 truncate font-medium">
																				{issue.title}
																			</span>
																			{issue.dueDate ? (
																				<span className="shrink-0 text-[11px] font-medium text-text-small">
																					{format(issue.dueDate, "HH:mm")}
																				</span>
																			) : null}
																			{issue.assignee ? (
																				<EntityIdentity
																					kind="user"
																					variant="avatar"
																					size="xs"
																					name={
																						issue.assignee.orgMember.user
																							?.name ?? "?"
																					}
																					image={
																						issue.assignee.orgMember.user?.image
																					}
																					lastSeenAt={
																						issue.assignee.orgMember.user
																							?.lastSeenAt
																					}
																				/>
																			) : (
																				<UserCircleIcon className="size-4 shrink-0 text-icons" />
																			)}
																		</Link>
																	</TooltipTrigger>
																	<TooltipContent>
																		<div className="flex flex-col gap-1">
																			<span>
																				{fancyIssueId(
																					issue.friendlyId,
																					issue.team.slug
																				)}
																				: {issue.title}
																			</span>
																			<span className="text-text-small">
																				Due{" "}
																				{issue.dueDate
																					? formatDate(issue.dueDate)
																					: "not set"}
																			</span>
																		</div>
																	</TooltipContent>
																</Tooltip>
															</TooltipProvider>
														</div>
													))}
												</div>
											</div>
										</motion.div>
									)
								})}
							</AnimatePresence>
						</div>
					</div>
					{activeIssue ? (
						<div className="pointer-events-none fixed bottom-20 right-5 z-40 w-64 opacity-90">
							<div className="flex items-center gap-1.5 rounded-md border border-primary/80 bg-background-tertiary px-2 py-1 text-xs text-text-main shadow-lg">
								<StatusIcon status={activeIssue.status} className="size-3.5" />
								<span className="min-w-0 flex-1 truncate font-medium">
									{activeIssue.title}
								</span>
							</div>
						</div>
					) : null}
				</ScrollArea>
			) : (
				<div className="flex flex-1 items-center justify-center px-4 text-center">
					<h1 className="text-xl font-semibold text-text-main">
						No issues due in {monthLabel}. Choose another!
					</h1>
				</div>
			)}
			<UnsavedChanges
				isVisible={pendingCount > 0}
				canSave={!editIssue.isPending}
				onSave={async () => {
					await Promise.all(
						Object.entries(pendingDueDates).map(([id, dueDate]) => {
							const issue = teamIssueData.issues.find(
								(issue) => issue.id === id
							)
							if (!issue) return Promise.resolve()
							return editIssue.mutateAsync({
								id,
								teamId: issue.teamId,
								dueDate
							})
						})
					)
					setPendingDueDates({})
					await Promise.all([
						utils.issues.getTeamIssues.invalidate(),
						utils.issues.getUserIssues.invalidate()
					])
					toast.success("Due dates updated")
				}}
				onReset={async () => {
					setPendingDueDates({})
				}}
			/>
		</div>
	)
}
